import { Chat, ChatPreview, ChatType } from "@/components/chat/types";
import { chatService } from "@/services/chatService";

// Welcome Bot constants
const WELCOME_BOT_ID = "welcome-bot";
const WELCOME_BOT = {
  id: WELCOME_BOT_ID,
  name: "Welcome Bot",
  avatarUrl: "https://api.dicebear.com/6.x/bottts/svg?seed=welcome",
  email: "welcome@step.app",
  role: "individual" as const,
  teamType: "general" as const,
  skills: [],
};

/**
 * Generate welcome chat for users with no chats
 */
const generateWelcomeChat = (): ChatPreview => ({
  id: "welcome-chat",
  user: WELCOME_BOT,
  lastMessage: "Welcome to Step! You're all set up. Invite team members to start chatting.",
  timestamp: new Date(),
  unread: false,
  type: "individual" as ChatType,
});

/**
 * Create a new chat
 */
export const newChat = async (
  type: string,
  groupName: string
): Promise<Chat | null> => {
  try {
    console.log("Creating chat with type:", type, "name:", groupName);

    const chat = await chatService.createChat({
      name: groupName,
      type: type as 'individual' | 'group',
      participantIds: [], // Will be added separately
    });

    return chat || null;
  } catch (error) {
    console.error("Failed to create chat", error);
    return null;
  }
};

/**
 * Fetch real chat previews from API
 * COMPLETE VERSION - Uses backend API
 */
export const fetchRealChatPreviews = async (
  userId: string
): Promise<ChatPreview[]> => {
  try {
    const response = await chatService.getChats();

    // API returns data directly, not wrapped in { success, data }
    const chats = Array.isArray(response) ? response : (response?.chats || []);
    
    // If no chats, return welcome bot chat
    if (chats.length === 0) {
      return [generateWelcomeChat()];
    }
    
    // Transform backend chat data to ChatPreview format
    return chats.map((chat: any) => {
      // For individual chats, get the other participant
      // Backend returns otherParticipants array for individual chats
      const otherParticipant = chat.type === 'individual' 
        ? chat.otherParticipants?.[0] 
        : null;
      
      // For group chats, use first participant as display user
      const displayUser = chat.type === 'group' && chat.participants?.length > 0
        ? chat.participants[0]?.user
        : otherParticipant;
      
      // Transform participants to UserProfile format for group chats
      const members = chat.type === 'group' && chat.participants
        ? chat.participants.map((p: any) => ({
            id: p.user?.id || p.userId,
            name: p.user?.displayName || p.user?.name || 'Unknown',
            avatarUrl: p.user?.avatarUrl || null,
            email: p.user?.email || '',
            role: 'individual' as const,
            teamType: 'general' as const,
            skills: [],
          }))
        : undefined;

      return {
        id: chat.id,
        user: displayUser ? {
          id: displayUser.id || displayUser.userId,
          name: displayUser.displayName || displayUser.name || 'Unknown',
          avatarUrl: displayUser.avatarUrl || null,
          email: displayUser.email || '',
          role: "individual" as const,
          teamType: "general" as const,
          skills: [],
        } : {
          id: 'unknown',
          name: 'Unknown',
          avatarUrl: null,
          email: '',
          role: 'individual' as const,
          teamType: 'general' as const,
          skills: [],
        },
        lastMessage: chat.lastMessage?.content || "",
        timestamp: chat.lastMessageAt ? new Date(chat.lastMessageAt) : chat.createdAt ? new Date(chat.createdAt) : new Date(),
        unread: (chat.unreadCount || 0) > 0,
        type: chat.type as ChatType,
        groupName: chat.groupName || undefined,
        members,
      };
    });
  } catch (error) {
    console.error("Failed to fetch chat previews", error);
    return [generateWelcomeChat()]; // Return welcome chat on error too
  }
};
